import {
  createFormulaDriver,
  createFreestyleDriver,
  createGroup,
  deleteDriver,
  duplicateDriver,
  updateDriverOrdering,
  updateFormulaDriver,
  updateFreestyleDriver,
  updateGroup
} from '../../predict/src/core/api/non-financial-drivers';
import type {
  FormulaNonFinancialDriverSaveData,
  FormulaNonFinancialDriverUpdateData,
  FreestyleNonFinancialDriverSaveData,
  FreestyleNonFinancialDriverUpdateData,
  NonFinancialDriverDuplicateData,
  NonFinancialDriverGroupSaveData,
  NonFinancialDriverGroupUpdateData,
  NonFinancialDriverNodeList
} from '../../predict/src/core/api/types/non-financial-drivers';
import { queryClient } from '../core/queryClient';
import {
  driverDetailQueryKey,
  driversDataQueryKey,
  driversOrderQueryKey,
  formulaOptionsQueryKey
} from '../queries/nonFinancialDrivers';
import { createMutation } from '../utils/mutations';

type CreateFormulaDriverResult = Awaited<ReturnType<typeof createFormulaDriver>>;
type CreateFreestyleDriverResult = Awaited<ReturnType<typeof createFreestyleDriver>>;
type CreateGroupResult = Awaited<ReturnType<typeof createGroup>>;
type DeleteDriverResult = Awaited<ReturnType<typeof deleteDriver>>;
type DuplicateDriverResult = Awaited<ReturnType<typeof duplicateDriver>>;
type UpdateDriverOrderingResult = Awaited<ReturnType<typeof updateDriverOrdering>>;
type UpdateFormulaDriverResult = Awaited<ReturnType<typeof updateFormulaDriver>>;
type UpdateFreestyleDriverResult = Awaited<ReturnType<typeof updateFreestyleDriver>>;
type UpdateGroupResult = Awaited<ReturnType<typeof updateGroup>>;

export const invalidateDriverQueries = (workspaceId: string, organisationId: string) => {
  queryClient.invalidateQueries({ queryKey: driversDataQueryKey(workspaceId, organisationId) });
  queryClient.invalidateQueries({ queryKey: driversOrderQueryKey(workspaceId, organisationId) });
  queryClient.invalidateQueries({ queryKey: formulaOptionsQueryKey(workspaceId, organisationId) });
};

export const createFormulaDriverMutation = (workspaceId: string, organisationId: string) =>
  createMutation<FormulaNonFinancialDriverSaveData, CreateFormulaDriverResult>({
    mutationFn: data => createFormulaDriver(workspaceId, organisationId, data),
    onSuccess: data => {
      invalidateDriverQueries(workspaceId, organisationId);
      queryClient.setQueryData(driverDetailQueryKey(workspaceId, organisationId, data.id), data);
    }
  });

export const createFreestyleDriverMutation = (workspaceId: string, organisationId: string) =>
  createMutation<FreestyleNonFinancialDriverSaveData, CreateFreestyleDriverResult>({
    mutationFn: data => createFreestyleDriver(workspaceId, organisationId, data),
    onSuccess: data => {
      invalidateDriverQueries(workspaceId, organisationId);
      queryClient.setQueryData(driverDetailQueryKey(workspaceId, organisationId, data.id), data);
    }
  });

export const createGroupMutation = (workspaceId: string, organisationId: string) =>
  createMutation<NonFinancialDriverGroupSaveData, CreateGroupResult>({
    mutationFn: data => createGroup(workspaceId, organisationId, data),
    onSuccess: () => invalidateDriverQueries(workspaceId, organisationId)
  });

export const duplicateDriverMutation = (workspaceId: string, organisationId: string) =>
  createMutation<{ driverId: string; data: NonFinancialDriverDuplicateData }, DuplicateDriverResult>({
    mutationFn: input => duplicateDriver(workspaceId, organisationId, input.driverId, input.data),
    onSuccess: data => {
      invalidateDriverQueries(workspaceId, organisationId);
      queryClient.setQueryData(driverDetailQueryKey(workspaceId, organisationId, data.data.id), data);
    }
  });

export const updateFormulaDriverMutation = (workspaceId: string, organisationId: string) =>
  createMutation<{ driverId: string; data: FormulaNonFinancialDriverUpdateData }, UpdateFormulaDriverResult>({
    mutationFn: input => updateFormulaDriver(workspaceId, organisationId, input.driverId, input.data),
    onSuccess: data => {
      invalidateDriverQueries(workspaceId, organisationId);
      queryClient.setQueryData(driverDetailQueryKey(workspaceId, organisationId, data.id), data);
    }
  });

export const updateFreestyleDriverMutation = (workspaceId: string, organisationId: string) =>
  createMutation<{ driverId: string; data: FreestyleNonFinancialDriverUpdateData }, UpdateFreestyleDriverResult>({
    mutationFn: input => updateFreestyleDriver(workspaceId, organisationId, input.driverId, input.data),
    onSuccess: data => {
      invalidateDriverQueries(workspaceId, organisationId);
      queryClient.setQueryData(driverDetailQueryKey(workspaceId, organisationId, data.id), data);
    }
  });

export const updateGroupMutation = (workspaceId: string, organisationId: string) =>
  createMutation<{ groupId: string; data: NonFinancialDriverGroupUpdateData }, UpdateGroupResult>({
    mutationFn: input => updateGroup(workspaceId, organisationId, input.groupId, input.data),
    onSuccess: () => invalidateDriverQueries(workspaceId, organisationId)
  });

export const updateDriverOrderingMutation = (workspaceId: string, organisationId: string) =>
  createMutation<NonFinancialDriverNodeList[], UpdateDriverOrderingResult>({
    mutationFn: data => updateDriverOrdering(workspaceId, organisationId, data),
    onSuccess: () => invalidateDriverQueries(workspaceId, organisationId)
  });

export interface UpdateGroupAndOrderingInput {
  groupId: string;
  groupData: NonFinancialDriverGroupUpdateData;
  orderingData: NonFinancialDriverNodeList[];
}

export const updateGroupAndOrderingMutation = (workspaceId: string, organisationId: string) =>
  createMutation<UpdateGroupAndOrderingInput, void>({
    mutationFn: async input => {
      // Execute both updates in parallel
      await Promise.all([
        updateGroup(workspaceId, organisationId, input.groupId, input.groupData),
        updateDriverOrdering(workspaceId, organisationId, input.orderingData)
      ]);
    },
    onSuccess: () => {
      return invalidateDriverQueries(workspaceId, organisationId)
    }
  });

export const deleteDriverMutation = (workspaceId: string, organisationId: string) =>
  createMutation<string, DeleteDriverResult>({
    mutationFn: driverId => deleteDriver(workspaceId, organisationId, driverId),
    onSuccess: () => invalidateDriverQueries(workspaceId, organisationId)
  });
